import React, { useEffect, useState } from "react";
import { maintenanceApi, ProcDeviceDetail } from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { stageMeta, fmtDate, fmtDateTime, errMsg } from "./procUtils";
import OnboardForm from "./OnboardForm";
import { X, Lock, Radio, ExternalLink } from "lucide-react";
import { useNotice } from "../shared/useNotice";

interface Props {
  deviceId: number;
  onClose: () => void;
  onChanged: (msg?: string) => void;
}

/** Full procurement record for one device, plus the actions available at its stage. */
const DeviceDrawer: React.FC<Props> = ({ deviceId, onClose, onChanged }) => {
  const [d, setD] = useState<ProcDeviceDetail | null>(null);
  const [error, setError] = useNotice("error", "");
  const [busy, setBusy] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);

  const canEdit = canEditMaintenance();

  useEffect(() => {
    maintenanceApi
      .procGetDevice(deviceId)
      .then((r) => setD(r.data))
      .catch((e) => setError(errMsg(e, "Failed to load device")));
  }, [deviceId]);

  const handleRetire = async () => {
    const reason = window.prompt(
      "Retire this device — nothing is deleted. The record, its ID and all history stay exactly where they are.\n\nReason:"
    );
    if (!reason) return;
    setBusy(true);
    try {
      await maintenanceApi.procRetireDevice(deviceId, reason);
      onChanged(`${d?.unique_id} retired`);
    } catch (e: any) {
      setError(errMsg(e, "Failed to retire device"));
      setBusy(false);
    }
  };

  if (!d) {
    return (
      <div className="proc-drawer-wrap" onClick={onClose}>
        <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="proc-drawer-body">
            {error ? <p className="maint-form-error">{error}</p> : <p className="maint-loading">Loading…</p>}
          </div>
        </div>
      </div>
    );
  }

  const s = stageMeta(d.stage);
  const booted = !!d.first_boot_at;

  return (
    <div className="proc-drawer-wrap" onClick={onClose}>
      <div className="proc-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="proc-drawer-head">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div className="proc-mini">
                {d.batch_name || "no batch"}
                {d.manufacturer_name ? ` · ${d.manufacturer_name}` : ""}
              </div>
              <div className="proc-mono" style={{ fontSize: 22, fontWeight: 700 }}>
                {d.unique_id}
              </div>
            </div>
            <button className="maint-btn-icon" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ marginTop: 8 }}>
            <span className={`proc-pill ${s.cls}`}>{s.label}</span>
            {d.order_no && <span className="proc-mini" style={{ marginLeft: 8 }}>on {d.order_no}</span>}
            {d.retire_reason && (
              <div className="proc-mini" style={{ color: "#c62828", marginTop: 6 }}>{d.retire_reason}</div>
            )}
          </div>
        </div>

        <div className="proc-drawer-body">
          {error && <p className="maint-form-error">{error}</p>}

          {/* Identity — minted, immutable */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag auto">Auto · minted</span> Identity
            </div>
            <div className="gb">
              <div className="proc-kv">
                <span className="k">Device ID</span>
                <span className="v proc-mono">{d.unique_id}</span>
                <span className="k">QR URL</span>
                <span className="v" style={{ fontSize: 11.5 }}>{d.qr_url}</span>
                <span className="k">WebSocket URL</span>
                <span className="v proc-mono" style={{ fontSize: 11 }}>{d.ws_url}</span>
              </div>
              <div className="proc-note" style={{ display: "flex", alignItems: "center", gap: 6, color: "#8a8fd0" }}>
                <Lock size={12} /> Printed on the unit — immutable. Never renamed, reused or{" "}
                <code>old-</code>prefixed.
              </div>
            </div>
          </div>

          {/* Hardware — from OCPP BootNotification */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag boot">Auto · OCPP boot</span> Reported by the device
            </div>
            <div className="gb">
              {booted ? (
                <div className="proc-kv">
                  <span className="k">Vendor</span><span className="v">{d.cp_vendor || "—"}</span>
                  <span className="k">Model</span><span className="v">{d.cp_model || "—"}</span>
                  <span className="k">Serial no.</span><span className="v proc-mono">{d.serial_no || "—"}</span>
                  <span className="k">Firmware</span><span className="v">{d.firmware || "—"}</span>
                  <span className="k">SIM ICCID</span><span className="v proc-mono" style={{ fontSize: 11 }}>{d.iccid || "—"}</span>
                  <span className="k">Meter serial</span><span className="v proc-mono">{d.meter_serial || "—"}</span>
                  <span className="k">First boot</span><span className="v">{fmtDateTime(d.first_boot_at)}</span>
                </div>
              ) : (
                <div className="proc-banner info" style={{ margin: 0 }}>
                  <Radio size={16} />
                  <span>
                    Not yet connected — vendor, model, serial, firmware and SIM fill themselves in
                    from the first OCPP <code>BootNotification</code>. Nothing here is typed.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Procurement / logistics */}
          <div className="proc-group">
            <div className="gh">
              <span className="proc-tag req">Procurement</span> Commercial &amp; logistics
            </div>
            <div className="gb">
              <div className="proc-kv">
                <span className="k">Manufacturer</span><span className="v">{d.manufacturer_name || "—"}</span>
                <span className="k">PO / order ref</span><span className="v">{d.po_ref || "—"}</span>
                <span className="k">Order</span><span className="v">{d.order_no || "—"}</span>
                <span className="k">Dispatched</span><span className="v">{fmtDate(d.dispatched_on)}</span>
                <span className="k">Received</span><span className="v">{fmtDate(d.received_on)}</span>
                <span className="k">Condition</span><span className="v">{d.condition || "—"}</span>
                <span className="k">Connectivity</span>
                <span className="v">
                  {d.connectivity === "sim" ? "SIM" : d.connectivity === "wifi" ? "WiFi"
                    : d.connectivity === "lan" ? "Ethernet" : "unknown"}
                </span>
                {d.sim_msisdn && (
                  <>
                    <span className="k">SIM</span>
                    <span className="v proc-mono">
                      {d.sim_msisdn}{" "}
                      {d.sim_days_left != null && (
                        <span className={`proc-pill ${d.sim_days_left < 0 ? "red" : d.sim_days_left <= 7 ? "orange" : "green"}`}>
                          {d.sim_days_left < 0 ? `expired ${Math.abs(d.sim_days_left)}d ago` : `${d.sim_days_left}d left`}
                        </span>
                      )}
                    </span>
                  </>
                )}
                {d.warranty_end && (<><span className="k">Warranty till</span><span className="v">{fmtDate(d.warranty_end)}</span></>)}
                {d.bis_cert && (<><span className="k">BIS cert</span><span className="v proc-mono">{d.bis_cert}</span></>)}
                {d.arai_cert && (<><span className="k">ARAI cert</span><span className="v proc-mono">{d.arai_cert}</span></>)}
                {d.evyatra_id && (<><span className="k">Evyatra ID</span><span className="v">{d.evyatra_id}</span></>)}
              </div>
            </div>
          </div>

          {/* Deployment */}
          {d.devices_main_id && (
            <div className="proc-group">
              <div className="gh">
                <span className="proc-tag req">Deployment</span> Live in the Devices console
              </div>
              <div className="gb">
                <div className="proc-kv">
                  <span className="k">Display name</span><span className="v">{d.display_name || "—"}</span>
                  <span className="k">Station</span><span className="v">{d.station_name || "—"}</span>
                </div>
                <a
                  className="maint-btn-link"
                  href={`/maintenance/devices/${d.devices_main_id}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10 }}
                >
                  Open device #{d.devices_main_id} <ExternalLink size={13} />
                </a>
              </div>
            </div>
          )}

          {canEdit && (
            <div className="maint-form-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
              {d.stage === "received" && (
                <button className="maint-btn-primary" onClick={() => setShowOnboard(true)} disabled={busy}>
                  Onboard this device →
                </button>
              )}
              {d.stage !== "retired" && d.stage !== "onboarded" && (
                <button className="maint-btn-danger" onClick={handleRetire} disabled={busy}>
                  Retire / RMA…
                </button>
              )}
            </div>
          )}

          {d.stage === "generated" && (
            <p className="proc-note">
              Not yet ordered. Select it in the pool and <b>Add to order</b> to send it to a
              manufacturer.
            </p>
          )}
          {d.stage === "dispatched" && (
            <p className="proc-note">
              Out with the manufacturer. It becomes onboardable once a goods-receipt marks it
              received — or automatically the first time it connects over OCPP.
            </p>
          )}
        </div>
      </div>

      {showOnboard && (
        <OnboardForm
          device={d}
          onClose={() => setShowOnboard(false)}
          onDone={(res) =>
            onChanged(`${res.unique_id} onboarded — device #${res.devices_main_id} created`)
          }
        />
      )}
    </div>
  );
};

export default DeviceDrawer;
